import { useState } from 'react';
import { Funcionario } from '@/context/FuncionariosContext';
import { calcularProgressao, getStatusColor } from '@/utils/progressaoUtils';
import { useAppSettings } from '@/context/AppSettingsContext';

interface CarreiraGroupProps {
  carreira: string;
  funcionarios: Funcionario[];
  pontosPorFuncionario: Record<string, number>;
  limiarPorFuncionario: Record<string, number>;
  regraEspecialPorFuncionario: Record<string, boolean>;
  onRegistar: (f: Funcionario) => void;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

export default function CarreiraGroup({
  carreira,
  funcionarios,
  pontosPorFuncionario,
  limiarPorFuncionario,
  regraEspecialPorFuncionario,
  onRegistar,
}: CarreiraGroupProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { settings } = useAppSettings();

  const elegiveis = funcionarios.filter((f) => {
    const pontos = pontosPorFuncionario[f.id] ?? 0;
    const limiar = limiarPorFuncionario[f.id] ?? settings.pontos_progressao;
    return calcularProgressao(pontos, f.nivelAtual, limiar).status === 'pronto';
  });

  const avatarColors = [
    'bg-rose-100 text-rose-700',
    'bg-violet-100 text-violet-700',
    'bg-amber-100 text-amber-700',
    'bg-teal-100 text-teal-700',
    'bg-sky-100 text-sky-700',
    'bg-orange-100 text-orange-700',
  ];

  return (
    <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
      {/* Header do grupo */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-zinc-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-briefcase-line text-zinc-600 text-sm" />
          </div>
          <div className="text-left">
            <p className="text-[13px] font-semibold text-zinc-800">{carreira}</p>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              {funcionarios.length} funcionário{funcionarios.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {elegiveis.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
              {elegiveis.length} elegíve{elegiveis.length === 1 ? 'l' : 'is'}
            </span>
          )}
          {/* Barra de progresso mini */}
          <div className="w-20 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-400 rounded-full transition-all"
              style={{ width: funcionarios.length > 0 ? `${(elegiveis.length / funcionarios.length) * 100}%` : '0%' }}
            />
          </div>
          <div className="w-5 h-5 flex items-center justify-center text-zinc-400">
            <i className={`${collapsed ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-base`} />
          </div>
        </div>
      </button>

      {/* Tabela de funcionários */}
      {!collapsed && (
        <div className="border-t border-zinc-100 overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-zinc-50/60">
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Nº</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Nome</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Sexo</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Última Progressão</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Nível</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest">Pontos</th>
                <th className="px-5 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-widest"></th>
              </tr>
            </thead>
            <tbody>
              {funcionarios.map((f, idx) => {
                const pontos = pontosPorFuncionario[f.id] ?? 0;
                const limiar = limiarPorFuncionario[f.id] ?? settings.pontos_progressao;
                const prog = calcularProgressao(pontos, f.nivelAtual, limiar);
                const colors = getStatusColor(prog.status);
                const temRegraEsp = regraEspecialPorFuncionario[f.id] ?? false;
                const colorClass = avatarColors[idx % avatarColors.length];
                const initials = f.nome
                  .split(' ')
                  .filter((_, i, arr) => i === 0 || i === arr.length - 1)
                  .map((n) => n[0])
                  .join('');

                return (
                  <tr key={f.id} className="border-t border-zinc-50 hover:bg-zinc-50/60 transition-colors">
                    <td className="px-5 py-3">
                      <span className="font-mono text-[11px] text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded">#{f.id}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold flex-shrink-0 ${colorClass}`}>
                          {initials}
                        </div>
                        <div>
                          <p className="text-[12px] font-semibold text-zinc-800 whitespace-nowrap">{f.nome}</p>
                          {temRegraEsp && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
                              <i className="ri-award-line text-[9px]" />
                              18 anos
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${f.sexo === 'F' ? 'ri-women-line text-rose-400' : 'ri-men-line text-sky-400'} text-sm`} />
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-[12px] text-zinc-500">{formatDate(f.ultimaProgressao)}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px] text-zinc-500">{f.nivelAtual}</span>
                        <div className="w-4 h-4 flex items-center justify-center text-emerald-500">
                          <i className="ri-arrow-right-line text-xs" />
                        </div>
                        <span className="text-[12px] font-semibold text-zinc-800">{f.novoNivel2025}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1 min-w-[110px]">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-[12px] font-bold ${colors.text}`}>{pontos} pts</span>
                          {prog.status === 'pronto' ? (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${colors.badge}`}>
                              Elegível
                            </span>
                          ) : (
                            <span className="text-[10px] text-zinc-400">/ {limiar}</span>
                          )}
                        </div>
                        <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${colors.bar}`}
                            style={{ width: `${prog.percentagem}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {prog.status === 'pronto' && (
                        <button
                          onClick={() => onRegistar(f)}
                          className="whitespace-nowrap flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-arrow-up-circle-line text-xs" />
                          Registar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
